import { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { postMedium } from 'story-media/api';
import { uploadMedium } from 'story-media/upload-medium';
import { getSWR, patch } from 'utils/api';
import useSWR from 'swr';
import useEntityActionCreators from 'entities/hooks/use-entity-action-creators';
import ImageUploader from './ImageUploader';
import useEntity from 'entities/hooks/use-entity';
import BufferingOverlay from 'app/modal/BufferingOverlay';

function ImageEditor({ photoLayer, revalidateBookStories, imageSrc, setSelectedLayer, setSelectedImageLayer, firstBookId, pageId }) {
    const [scale, setScale] = useState(0);
    const [photoScaling, setPhotoScalling] = useState(null);
    const [file, setFile] = useState(null);
    const [src, setSrc] = useState(null);
    const [frameSrc, setFrameSrc] = useState(null);

    const [uploadStatus, setUploadStatus] = useState(null);
    const [isSubmitting, setSubmitting] = useState(false);
    const [destinationCanvasId, setDestinationCanvasId] = useState(null);
    const [refreshInterval, setRefreshInterval] = useState(0);

    const { data, revalidate } = useSWR(destinationCanvasId ? `/v2/media/${destinationCanvasId}` : null, getSWR, { refreshInterval });
    const updateMediaDetails = data?.data?.data?.attributes;
    const destinationImageSrcExists = photoLayer?.['photo']?.id
    const { receiveDeletion, update } = useEntityActionCreators();
    const { entity: frameLayerImage } = useEntity({ type: 'images', id: photoLayer?.['photo'].id });
    const doesImageExist = Object.keys(imageSrc || {}).length > 0
    const doesFrameImageExist = Object.keys(frameLayerImage || {}).length > 0

    useEffect(() => {
        setUploadStatus(updateMediaDetails?.state);
    }, [updateMediaDetails?.state]);

    useEffect(() => {
        if (doesFrameImageExist)
            setFrameSrc(frameLayerImage?.['image-versions']?.[4]?.url || frameLayerImage?.tmpURL);
    }, [doesFrameImageExist]);

    useEffect(() => {
        if (
            uploadStatus === 'pending_upload' ||
            uploadStatus === 'uploaded' ||
            uploadStatus === 'processing'
        ) {
            setRefreshInterval(3000);
        }

        if (refreshInterval !== null && uploadStatus === 'finished') {
            setRefreshInterval(0);
            setSubmitting(false);
            revalidateBookStories();
        }
    }, [uploadStatus, setRefreshInterval]);

    useEffect(() => {
        if (doesImageExist) {
            setSrc(imageSrc?.['image-versions']?.[4]?.url || imageSrc?.tmpURL);
        }
    }, [doesImageExist, imageSrc]);

    const onDrop = async (val) => {
        if (val && val[0].type.includes('image')) {
            setFile(val[0]);
        } else {
            // setDropError(`Please add a image file`);
        }
    };

    useEffect(() => {
        if (imageSrc?.width && imageSrc?.left !== null && imageSrc?.top !== null)
            setPhotoScalling({
                x: imageSrc?.['left'] || 0,
                y: imageSrc?.['top'] || 0,
            });
        setScale(imageSrc?.zoom || 1);
    }, [imageSrc]);

    const { getRootProps, getInputProps } = useDropzone({ onDrop });


    const onImageSelection = async (val, selectedFile) => {
        const { scale: valScale, position, croppedPostion, croppedBlob = null } = val;
        const attributes = {};

        setUploadStatus('pending_upload');
        setSubmitting(true);
        try {
            let canvasUploadUrl = null
            let canvasImageId = null
            let blob = null
            let previewUrl = null
            const updatedAttrs = {}
            if (croppedBlob) {
                blob = new Blob([croppedBlob], { type: 'image/png' });
                blob.name = 'canvas-clip-image.png';
                const payload = await postMedium({
                    attrs: {
                        type: 'Image',
                        ownerType: "CelebrationPrintableLayer",
                        ownerId: photoLayer.id,
                        filename: blob.name,
                        previewFilename: ' ',
                    },
                });
                const { entities, result } = payload.data;
                const { 'upload-url': uploadUrl } = entities.images[result.images[0]];

                attributes.canvas_image_id = result.images[0];
                canvasUploadUrl = uploadUrl
                canvasImageId = result.images[0]
                updatedAttrs['canvas-image'] = { type: 'images', id: attributes.canvas_image_id }
            }

            if (canvasUploadUrl) {
                previewUrl = URL.createObjectURL(blob);
                updatedAttrs.tempURL = previewUrl;
                // Resolves rather than throwing, so a failed canvas upload no longer
                // skips the media patch below it.
                await uploadMedium({
                    url: canvasUploadUrl,
                    mediumId: canvasImageId,
                    mediumType: 'Image',
                    file: blob,
                });
            }
            await patch(`/v2/media/${imageSrc?.id}`, {
                data: {
                    attributes: {
                        'lock-version': imageSrc?.['lock-version'],
                        zoom: +valScale,
                        'crop-left': `${croppedPostion?.x}`,
                        'crop-top': `${croppedPostion?.y}`,
                        left: position?.x,
                        top: position?.y,
                        'crop-width': `${croppedPostion.width}%`,
                        'crop-height': `${croppedPostion.height}%`,
                        canvas_image_id: attributes?.canvas_image_id
                    }
                }
            })
            await patch(`/v3/books/${firstBookId}/pages/${pageId}/layers/${photoLayer?.id}`,
                {
                    data: {
                        attributes,
                    },
                }
            );
            if (selectedFile) {
                setDestinationCanvasId(attributes.photo_id)
            }
            else {
                setDestinationCanvasId(attributes.canvas_image_id)
            }

            revalidate()
            revalidateBookStories()
            setSelectedImageLayer(null);
            setRefreshInterval(3000)
            update({
                entityType: 'images',
                id: imageSrc?.id,
                attrs: {
                    'lock-version': imageSrc?.['lock-version'],
                    zoom: +valScale,
                    'crop-left': `${croppedPostion?.x}`,
                    'crop-top': `${croppedPostion?.y}`,
                    left: position?.x,
                    top: position?.y,
                    'crop-width': `${croppedPostion.width}%`,
                    'crop-height': `${croppedPostion.height}%`,
                    canvas_image_id: attributes?.canvas_image_id,
                    // Without this, `imageSrc.tmpURL` stays whatever it was before this
                    // crop (or undefined) until the real `image-versions` URL comes back
                    // from processing — and since `src` falls back to `tmpURL` while
                    // that's pending, reopening "Crop photo" immediately after cropping
                    // saw no src at all and showed the upload dropzone instead of the
                    // image. `previewUrl` is only set when a new canvas crop was
                    // actually uploaded (see above); falling back to the existing
                    // `tmpURL` otherwise avoids clobbering it with `null`.
                    tmpURL: previewUrl || imageSrc?.['image-versions']?.[4]?.url,
                },
            });

            setSelectedLayer(null)
            update({
                entityType: 'book-page-layers',
                id: photoLayer?.id,
                attrs: updatedAttrs,
            });
        }
        catch (err) {
            revalidate()
            revalidateBookStories()
            setSelectedImageLayer(null);
            setSubmitting(false);
        }
    };

    return (
        <BufferingOverlay
            isBuffering={isSubmitting}
            bg={'rgba(0, 0, 0, 0.5)'}
        >
            <ImageUploader
                getRootProps={getRootProps}
                getInputProps={getInputProps}
                src={src}
                frameSrc={frameSrc}
                width={photoLayer?.width}
                height={photoLayer?.height}
                photoScaling={photoScaling}
                scale={scale}
                onImageSelection={onImageSelection}
                setPhotoScalling={setPhotoScalling}
                onImageDeletion={destinationImageSrcExists ? () => setSelectedImageLayer(null) : undefined}
                setScale={setScale}
                isSubmitting={isSubmitting}
                setSubmitting={setSubmitting}
                setFile={setFile}
            />
        </BufferingOverlay>
    );
}

export default ImageEditor;